import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./VisaDashboard.css";
import Header from "../Common/Layout/Header";
import { useAuth } from "../Common/Configurations/AuthContext";
import SupplierTabs from "./SupplierTabs"; // Import SupplierTabs

const VisaDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Visa Statistics Data
  const visaStats = [
    {
      title: "Total Visa Requests",
      value: "1,248",
      icon: "🛂",
      color: "visadashboard-stat-primary",
    },
    {
      title: "Approved Visas",
      value: "956",
      icon: "✅",
      color: "visadashboard-stat-success",
    },
    {
      title: "Pending Review",
      value: "182",
      icon: "⏳",
      color: "visadashboard-stat-warning",
    },
    {
      title: "Rejected Visas",
      value: "110",
      icon: "❌",
      color: "visadashboard-stat-danger",
    },
    {
      title: "Appointments",
      value: "87",
      icon: "📅",
      color: "visadashboard-stat-info",
    },
    {
      title: "Revenue",
      value: "INR8.75L",
      icon: "💰",
      color: "visadashboard-stat-revenue",
    },
  ];

  // Visa Prices Data
  const visaPrices = [
    {
      country: "USA",
      tourist: "INR15,000",
      business: "INR22,000",
      student: "INR18,000",
      work: "INR25,000",
    },
    {
      country: "UK",
      tourist: "INR12,000",
      business: "INR20,000",
      student: "INR16,000",
      work: "INR23,000",
    },
    {
      country: "Canada",
      tourist: "INR14,000",
      business: "INR21,000",
      student: "INR17,000",
      work: "INR24,000",
    },
    {
      country: "Australia",
      tourist: "INR13,000",
      business: "INR19,000",
      student: "INR15,000",
      work: "INR22,000",
    },
    {
      country: "Germany",
      tourist: "INR11,000",
      business: "INR18,000",
      student: "INR14,000",
      work: "INR20,000",
    },
  ];

  // Visa Requests Data
  const visaRequests = [
    {
      id: "VR-2023-001",
      applicant: "John Smith",
      passport: "A12345678",
      visaType: "Tourist Visa",
      country: "USA",
      submissionDate: "Nov 15, 2023",
      status: "pending",
      documents: 5,
      processingTime: "10-14 days",
      appointment: "Nov 20, 10:30 AM",
      fee: "INR12,500",
    },
    {
      id: "VR-2023-002",
      applicant: "Maria Garcia",
      passport: "B87654321",
      visaType: "Business Visa",
      country: "UK",
      submissionDate: "Nov 14, 2023",
      status: "approved",
      documents: 7,
      processingTime: "7-10 days",
      appointment: "Nov 18, 02:00 PM",
      fee: "INR18,000",
    },
    {
      id: "VR-2023-003",
      applicant: "Ahmed Khan",
      passport: "C34567891",
      visaType: "Student Visa",
      country: "Canada",
      submissionDate: "Nov 13, 2023",
      status: "under_review",
      documents: 8,
      processingTime: "15-20 days",
      appointment: "Nov 22, 11:00 AM",
      fee: "INR15,000",
    },
    {
      id: "VR-2023-004",
      applicant: "Emma Wilson",
      passport: "D23456789",
      visaType: "Work Visa",
      country: "Australia",
      submissionDate: "Nov 12, 2023",
      status: "approved",
      documents: 6,
      processingTime: "12-15 days",
      appointment: "Nov 16, 03:30 PM",
      fee: "INR22,000",
    },
    {
      id: "VR-2023-005",
      applicant: "Robert Chen",
      passport: "E98765432",
      visaType: "Tourist Visa",
      country: "France",
      submissionDate: "Nov 11, 2023",
      status: "rejected",
      documents: 4,
      processingTime: "8-12 days",
      appointment: "Nov 19, 09:00 AM",
      fee: "INR11,000",
    },
  ];

  // Appointments Data
  const appointments = [
    {
      id: "APT-001",
      applicant: "John Smith",
      type: "Visa Submission",
      date: "Nov 20, 2023",
      time: "10:30 AM",
      duration: "30 mins",
      status: "confirmed",
      location: "Main Office - Room 101",
      officer: "Mr. Sharma",
      documents: "Passport, Photos, Application",
    },
    {
      id: "APT-002",
      applicant: "Sophia Williams",
      type: "Interview",
      date: "Nov 22, 2023",
      time: "02:00 PM",
      duration: "45 mins",
      status: "confirmed",
      location: "Embassy Center - Room 205",
      officer: "Ms. Patel",
      documents: "All Original Documents",
    },
    {
      id: "APT-003",
      applicant: "David Miller",
      type: "Document Collection",
      date: "Nov 25, 2023",
      time: "11:15 AM",
      duration: "15 mins",
      status: "scheduled",
      location: "Main Office - Counter 3",
      officer: "Mr. Kumar",
      documents: "Passport Receipt",
    },
    {
      id: "APT-004",
      applicant: "Lisa Anderson",
      type: "Biometrics",
      date: "Nov 28, 2023",
      time: "09:45 AM",
      duration: "20 mins",
      status: "pending",
      location: "Biometrics Center",
      officer: "Ms. Gupta",
      documents: "Appointment Letter, ID Proof",
    },
  ];

  // Country-wise Distribution
  const countryDistribution = [
    { country: "USA", flag: "🇺🇸", requests: 320, successRate: 85 },
    { country: "UK", flag: "🇬🇧", requests: 280, successRate: 82 },
    { country: "Canada", flag: "🇨🇦", requests: 240, successRate: 88 },
    { country: "Australia", flag: "🇦🇺", requests: 220, successRate: 80 },
    { country: "Germany", flag: "🇩🇪", requests: 188, successRate: 78 },
  ];

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "visadashboard-status-approved";
      case "pending":
        return "visadashboard-status-pending";
      case "under_review":
        return "visadashboard-status-review";
      case "rejected":
        return "visadashboard-status-rejected";
      case "confirmed":
        return "visadashboard-status-confirmed";
      case "scheduled":
        return "visadashboard-status-scheduled";
      default:
        return "visadashboard-status-pending";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return "✅";
      case "pending":
        return "⏳";
      case "under_review":
        return "🔍";
      case "rejected":
        return "❌";
      case "confirmed":
        return "✓";
      case "scheduled":
        return "📅";
      default:
        return "⏳";
    }
  };

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* SupplierTabs inside a card like in SupplierVisaRequests */}
            <div className="card">
              <div className="p-2">
                {/* <SupplierTabs pageName="VISADASHBOARD" /> */}
              </div>
              
              <div className="card-body">
                {/* Your VisaDashboard content here */}
                <div className="visadashboard-dashboard">
                  {/* Main Content */}
                  <main className="visadashboard-main-content-fullscreen">
                    {/* Header */}
                    <header className="visadashboard-main-header">
                      <div className="visadashboard-header-content">
                        <div className="visadashboard-header-title">
                          <h1>Visa Management Dashboard</h1>
                          <p>Manage visa requests, appointments, and track processing</p>
                        </div>

                       
                      </div>
                    </header>

                    {/* Main Content Area */}
                    <div className="visadashboard-content-wrapper">
                      {/* Stats Grid */}
                      <div className="visadashboard-stats-grid">
                        {visaStats.map((stat, index) => (
                          <div
                            key={index}
                            className={`visadashboard-stat-card ${stat.color}`}
                          >
                            <div className="visadashboard-stat-header">
                              <div className="visadashboard-stat-icon-container">
                                <span className="visadashboard-stat-icon">{stat.icon}</span>
                              </div>
                            </div>

                            <h4 className="visadashboard-stat-value">{stat.value}</h4>
                            <p className="visadashboard-stat-title">{stat.title}</p>

                            <div className="visadashboard-stat-progress">
                              <div className="visadashboard-progress-bar" />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Main Content Grid */}
                      <div className="visadashboard-content-grid">
                        {/* Visa Requests Table */}
                        <div className="visadashboard-main-card">
                          <div className="visadashboard-card-header">
                            <div className="visadashboard-card-title">
                              <div className="visadashboard-card-icon visadashboard-requests-icon">
                                📋
                              </div>
                              <div>
                                <h3>Recent Visa Requests</h3>
                                <p>Detailed view of visa applications</p>
                              </div>
                            </div>
                            <button
                              className="visadashboard-view-all-btn"
                              onClick={() => navigate("/supplier/visarequests")}
                            >
                              View All Requests →
                            </button>
                          </div>

                          <div className="visadashboard-requests-table">
                            <table className="visadashboard-table">
                              <thead>
                                <tr>
                                  <th>Request ID</th>
                                  <th>Applicant</th>
                                  <th>Visa Type</th>
                                  <th>Status</th>
                                  <th>Documents</th>
                                  <th>Appointment</th>
                                  <th>Fee</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visaRequests.map((request, index) => (
                                  <tr key={index} className="visadashboard-table-row">
                                    <td>
                                      <div className="visadashboard-request-id">
                                        <span className="visadashboard-id-badge">
                                          {request.id}
                                        </span>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="visadashboard-applicant-info">
                                        <div className="visadashboard-applicant-name">
                                          {request.applicant}
                                        </div>
                                        <div className="visadashboard-passport-no">
                                          {request.passport}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="visadashboard-type-info">
                                        <span className="visadashboard-type-label">
                                          {request.visaType}
                                        </span>
                                        <span className="visadashboard-country-label">
                                          {request.country}
                                        </span>
                                      </div>
                                    </td>
                                    <td>
                                      <span
                                        className={`visadashboard-status-badge ${getStatusColor(
                                          request.status
                                        )}`}
                                      >
                                        <span className="visadashboard-status-icon">
                                          {getStatusIcon(request.status)}
                                        </span>
                                        <span>{request.status.replace("_", " ")}</span>
                                      </span>
                                    </td>
                                    <td>
                                      <div className="visadashboard-documents-count">
                                        <span className="visadashboard-documents-icon">
                                          📄
                                        </span>
                                        {request.documents} files
                                      </div>
                                    </td>
                                    <td>
                                      <div className="visadashboard-appointment-info">
                                        <div className="visadashboard-appointment-date">
                                          {request.appointment}
                                        </div>
                                        <div className="visadashboard-processing-time">
                                          {request.processingTime}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="visadashboard-fee-amount">
                                        {request.fee}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Right Sidebar - Stats & Appointments */}
                        <div className="visadashboard-sidebar-cards">
                          {/* Visa Prices */}
                          <div className="visadashboard-card">
                            <div className="visadashboard-card-header">
                              <div className="visadashboard-card-title">
                                <div className="visadashboard-card-icon visadashboard-types-icon">
                                  💰
                                </div>
                                <div>
                                  <h3>Visa Prices</h3>
                                  <p>Current visa fees by country</p>
                                </div>
                              </div>
                            </div>

                            <div className="visadashboard-prices-table">
                              <table className="visadashboard-table">
                                <thead>
                                  <tr>
                                    <th>Country</th>
                                    <th>Tourist</th>
                                    <th>Business</th>
                                    <th>Student</th>
                                    <th>Work</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visaPrices.map((price, index) => (
                                    <tr key={index} className="visadashboard-table-row">
                                      <td>
                                        <div className="visadashboard-country-info">
                                          <span className="visadashboard-country-name-small">
                                            {price.country}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="visadashboard-price-amount">
                                          {price.tourist}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="visadashboard-price-amount">
                                          {price.business}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="visadashboard-price-amount">
                                          {price.student}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="visadashboard-price-amount">
                                          {price.work}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Upcoming Appointments */}
                          <div className="visadashboard-card">
                            <div className="visadashboard-card-header">
                              <div className="visadashboard-card-title">
                                <div className="visadashboard-card-icon visadashboard-appointments-icon">
                                  📅
                                </div>
                                <div>
                                  <h3>Upcoming Appointments</h3>
                                  <p>Today & next 7 days</p>
                                </div>
                              </div>
                              <button
                                className="visadashboard-view-all-btn"
                                onClick={() => navigate("/supplier/visaappointments")}
                              >
                                View Calendar →
                              </button>
                            </div>

                            <div className="visadashboard-appointments-list">
                              {appointments.map((apt, index) => (
                                <div key={index} className="visadashboard-appointment-item">
                                  <div className="visadashboard-appointment-header">
                                    <div className="visadashboard-appointment-type">
                                      <span
                                        className={`visadashboard-type-badge ${getStatusColor(
                                          apt.type
                                        )}`}
                                      >
                                        {apt.type}
                                      </span>
                                    </div>
                                    <span
                                      className={`visadashboard-status-badge ${getStatusColor(
                                        apt.status
                                      )}`}
                                    >
                                      {apt.status}
                                    </span>
                                  </div>

                                  <div className="visadashboard-appointment-body">
                                    <div className="visadashboard-applicant-info">
                                      <div className="visadashboard-applicant-name">
                                        {apt.applicant}
                                      </div>
                                      <div className="visadashboard-appointment-id">
                                        {apt.id}
                                      </div>
                                    </div>

                                    <div className="visadashboard-appointment-details">
                                      <div className="visadashboard-appointment-time">
                                        <span className="visadashboard-date-icon">📅</span>
                                        <span>{apt.date}</span>
                                        <span className="visadashboard-time-separator">
                                          •
                                        </span>
                                        <span className="visadashboard-time-value">
                                          {apt.time}
                                        </span>
                                      </div>
                                      <div className="visadashboard-appointment-duration">
                                        <span className="visadashboard-duration-icon">
                                          ⏱️
                                        </span>
                                        {apt.duration}
                                      </div>
                                    </div>

                                    <div className="visadashboard-appointment-meta">
                                      <div className="visadashboard-appointment-location">
                                        <span className="visadashboard-location-icon">
                                          📍
                                        </span>
                                        {apt.location}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Country Performance */}
                      <div className="visadashboard-card visadashboard-full-width">
                        <div className="visadashboard-card-header">
                          <div className="visadashboard-card-title">
                            <div className="visadashboard-card-icon visadashboard-countries-icon">
                              🌐
                            </div>
                            <div>
                              <h3>Country-wise Performance</h3>
                              <p>Success rates and request volume by country</p>
                            </div>
                          </div>
                        </div>

                        <div className="visadashboard-countries-grid">
                          {countryDistribution.map((country, index) => (
                            <div key={index} className="visadashboard-country-card">
                              <div className="visadashboard-country-header">
                                <div className="visadashboard-country-flag">
                                  {country.flag}
                                </div>
                                <div className="visadashboard-country-name">
                                  {country.country}
                                </div>
                              </div>

                              <div className="visadashboard-country-stats">
                                <div className="visadashboard-country-stat">
                                  <span className="visadashboard-stat-label">
                                    Total Requests
                                  </span>
                                  <span className="visadashboard-stat-value">
                                    {country.requests}
                                  </span>
                                </div>
                                <div className="visadashboard-country-stat">
                                  <span className="visadashboard-stat-label">
                                    Success Rate
                                  </span>
                                  <span className="visadashboard-stat-value visadashboard-success-rate">
                                    {country.successRate}%
                                  </span>
                                </div>
                              </div>

                              <div className="visadashboard-country-progress">
                                <div className="visadashboard-progress-bar-container">
                                  <div
                                    className="visadashboard-progress-bar-fill"
                                    style={{ width: `${country.successRate}%` }}
                                  />
                                </div>
                                <div className="visadashboard-progress-labels">
                                  <span>Performance</span>
                                  <span>{country.successRate}%</span>
                                </div>
                              </div>

                              <button className="visadashboard-country-details-btn">
                                View Details
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </main>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaDashboard;